import { useServiciosStore } from './servicios';
import type { IUserConf, Prettify } from '~~/interfaces';
import { BASE_SERVICIO_SLUG, type TServiciosSlug } from '~~/constants';
import { useStorage } from '@vueuse/core';

export type UserPreferences = {
  estaciones: Record<TServiciosSlug, string[]>;
  limite: number;
  servicio: TServiciosSlug;
  theme: 'dark' | 'light';
};

export const useUserStore = defineStore('user', () => {
  const servicios = useServiciosStore();
  const router = useRouter();

  const defaultPreferences = {
    estaciones: {
      [servicios.servicio as string]: [] as string[],
    },
    limite: 10,
    servicio: servicios.servicio,
    theme: 'dark',
  };

  const cookie = useCookie('mb_user', {
    default: () => defaultPreferences,
    refresh: true,
  });

  const estacionesCookie = useStorage('mb_estaciones', {
    [servicios.servicio as string]: [] as string[],
  });

  const estacionesServicio = computed(() => estacionesCookie.value);
  const estaciones = computed({
    get: () => {
      const estacionesSet = estacionesCookie.value[servicio.value];
      return estacionesSet || [];
    },
    set: (estaciones: string[]) => {
      estacionesCookie.value = {
        ...estacionesCookie.value,
        [servicio.value]: estaciones,
      };
    },
  });

  const servicio = computed({
    get: () => servicios.servicio || BASE_SERVICIO_SLUG,
    set: (newSrv) => {
      if (servicios.servicio === newSrv) return;

      servicios.cambiarServicio({ servicio: newSrv });
      cookie.value = { ...cookie.value, servicio: newSrv };
      router.push(`/${newSrv}`);
    },
  });
  const limite = computed({
    get: () => cookie.value.limite,
    set: (limite: number) => {
      cookie.value.limite = limite;
    },
  });
  const loadedConfigs = ref(false);
  const v = ref(1);
  const theme = computed({
    get: () => cookie.value.theme,
    set: (theme: 'dark' | 'light') => (cookie.value.theme = theme),
  });

  async function initDB() {}

  async function setPreferencia({
    key,
    value,
  }: {
    key: Prettify<keyof IUserConf>;
    value: Prettify<IUserConf[keyof IUserConf]>;
  }) {
    if (!key) throw new Error('Faltan datos');
    if (!Object.keys(defaultPreferences).includes(key))
      throw new Error('Preferencia no encontrada');

    switch (key) {
      case 'limite':
        if (typeof value !== 'number') throw new Error('Valor no válido');
        cookie.value = { ...cookie.value, limite: value };
        break;
      case 'servicio':
        if (typeof value !== 'string') throw new Error('Valor no válido');
        servicio.value = value;
        break;
      case 'theme':
        if (typeof value !== 'string') throw new Error('Valor no válido');
        cookie.value = { ...cookie.value, theme: value };
        break;
      default:
        throw new Error('Preferencia no encontrada');
    }

    return;
  }

  function saveEstacion(
    estacion: string,
    servicio: TServiciosSlug = BASE_SERVICIO_SLUG
  ) {
    const remove = estaciones.value.includes(estacion);

    if (!servicio) return;
    if (!estacion) return;

    if (!remove) {
      if (!estacionesServicio.value[servicio]) {
        estacionesServicio.value[servicio] = [];
      }

      const estacionesSet = estacionesServicio.value[servicio];
      if (estacionesSet) {
        estacionesSet.push(estacion);
      }
    } else {
      if (estacionesServicio.value[servicio]) {
        const estacionesSet = estacionesServicio.value[servicio];
        if (estacionesSet) {
          estacionesServicio.value[servicio] = estacionesSet.filter(
            (e) => e !== estacion
          );
        }
      }
    }

    return estaciones.value;
  }

  return {
    estaciones,
    estacionesServicio,
    servicio,
    limite,
    loadedConfigs,
    v,
    theme,

    initDB,
    setPreferencia,
    saveEstacion,
  };
});
